import { useRef, useState } from "react";
import styled from "styled-components";
import { Connection, Id } from "./types";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Column from "../../../../components/Column";
import Row from "../../../../components/Row";
import ProfilePhoto from "../../../../components/ProfilePhoto";
import Text from "../../../../components/Text";
import { styles } from "../../../../styles/themes/style";
import AppTag from "../../../../fragments/AppTag";
import ConnectionActionDropdown from "../../../../fragments/dropdowns/ConnectionActionDropdown";
import SvgFragment from "../../../../fragments/SvgFragment";
import TooltipWrapper from "../../../../components/TooltipWrapper";
import { useConnectionsHandlers } from "../../../../services/actions/connections/useConnectionsHandlers";
import { useAppStore } from "../../../../stores/application/useAppStore";
import { useAuthStore } from "../../../../stores/auth/useAuthStore";
import { IKanban } from "../../../../interfaces/models/kanban";

interface Props {
  connection: Connection;
  removeConnection: (id: Id) => void;
  connectionClicked?: (connection: Connection) => void;
  currentKanbanId?: string; // ID of the currently active kanban
  pipelineKanbans?: IKanban[];
  activePipeline?: IKanban;
}

/** kanban-with-connections uses `p[]`; /profiles/:id/tags returns flat fields — support both */
const getConnectionProfileField = (conn: any, key: string) => {
  const fromP = conn?.p?.find((x: { k: string }) => x.k === key)?.v;
  if (fromP !== undefined && fromP !== null && fromP !== "") return fromP;
  return conn?.[key];
};

const ConnectionCard = ({
  connection,
  connectionClicked,
  removeConnection,
  currentKanbanId,
  pipelineKanbans,
  activePipeline,
}: Props) => {
  const [mouseIsOver, setMouseIsOver] = useState(false);
  const headlineRef = useRef<HTMLDivElement>(null);
  const companyRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const { visitConnectionAndUpdateHandler, messageClickHandler } =
    useConnectionsHandlers();
  const {
    store: { extensionInstalled, extensionVersion },
  } = useAppStore();
  const {
    store: { isLoggedToLinkedin, workspace, user },
  } = useAuthStore();

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: connection.id,
    data: { type: "Connection", connection },
  });

  // Filter kanban tags to show only the one that belongs to the current active kanban
  const connectionKanbanTag = connection.connection?.tags.filter(
    (item) => item.forKanban === true && (!currentKanbanId || item.kanban === currentKanbanId)
  );
  const isExtensionVersionCorrect =
    extensionVersion === process.env.REACT_APP_VERSION;
  const isMyConnection = (connection.connection as any)?.user === user?._id;
  const hasLinkedIn1stTag = connection.connection?.tags?.some(
    (tag: any) => tag.title === "LinkedIn 1st" || tag.title === "New Connection"
  );
  const isLinkedInConnectedInKanban = isMyConnection && hasLinkedIn1stTag;
  const canMessageInKanban =
    isLinkedInConnectedInKanban &&
    !workspace?.isStarter &&
    isLoggedToLinkedin &&
    extensionInstalled &&
    isExtensionVersionCorrect &&
    !!connection.connection?.publicIdentifier;

  const messageIconTooltipText = isLinkedInConnectedInKanban
    ? canMessageInKanban
      ? "Message"
      : !extensionInstalled
        ? "Download extension"
        : "Message"
    : "Connect";

  const handleVisitProfile = (e: any) => {
    e.stopPropagation();
    if (!connection.connection) return;
    visitConnectionAndUpdateHandler(connection.connection);
  };

  const handleMessage = async (e: any) => {
    e.stopPropagation();
    if (!connection.connection || !canMessageInKanban) return;
    await messageClickHandler(e, connection.connection);
  };

  const isTextTruncated = (ref: React.RefObject<HTMLDivElement | null>) => {
    const element = ref.current;
    if (!element) return false;

    return (
      element.scrollWidth > element.clientWidth ||
      element.scrollHeight > element.clientHeight
    );
  };

  return (
    <CardWrapper
      ref={setNodeRef}
      style={{ transition, transform: CSS.Transform.toString(transform) }}
      isDragging={isDragging}
      onClick={() => connectionClicked && connectionClicked(connection)}
      onMouseEnter={() => setMouseIsOver(true)}
      onMouseLeave={() => setMouseIsOver(false)}
      {...attributes}
      {...listeners}
    >
      <InfoBoxColumn gap="12px">
        <WrappingRow>
          {connectionKanbanTag?.[0]?.forKanban && (
            <AppTag
              key={connectionKanbanTag?.[0]?._id}
              tag={connectionKanbanTag[0]}
            />
          )}
          {connection.connection && (
            <TopRightActionsRow gap="0" onClick={(e: any) => e.stopPropagation()}>
              <CardIconsGroup $visible={mouseIsOver}>
                <TooltipWrapper tooltipText={messageIconTooltipText}>
                  <CardActionIconButton
                    onClick={
                      isLinkedInConnectedInKanban ? handleMessage : handleVisitProfile
                    }
                    disabled={isLinkedInConnectedInKanban && !canMessageInKanban}
                  >
                    <SvgFragment type={isLinkedInConnectedInKanban ? "message" : "linkedin"} />
                  </CardActionIconButton>
                </TooltipWrapper>
              </CardIconsGroup>
              <div>
                <ConnectionActionDropdown 
                  connection={connection.connection} 
                  fromPipeline 
                  hidePrimaryActions
                  pipelineKanbans={pipelineKanbans}
                  activePipeline={activePipeline}
                  onRemoveFromPipeline={() => removeConnection(connection.id)}
                />
              </div>
            </TopRightActionsRow>
          )}
        </WrappingRow>
        <Row gap="8px">
          <ProfilePhotoColumn>
            <ProfilePhoto
              source={connection.connection?.profilePicture}
              capitalLetters={
                !connection.connection?.profilePicture
                  ? `${connection.connection?.firstName
                      ?.charAt(0)
                      .toUpperCase()}${connection.connection?.lastName
                      ?.charAt(0)
                      .toUpperCase()}`
                  : undefined
              }
            />
          </ProfilePhotoColumn>
          <Column style={{ minWidth: 0, flex: 1 }}>
            <Text $active $bold className="kanban-connection-card-name-field">
              {connection.connection?.firstName +
                " " +
                connection.connection?.lastName}
            </Text>
            <Row style={{ width: '100%', minWidth: 0 }}>
              <Text
                ref={headlineRef}
                $ellipsis="1"
                showTooltip={isTextTruncated(headlineRef)}
              >
                {connection.connection?.headline}
              </Text>
            </Row>
          </Column>
        </Row>
        <Row gap="8px">
          <Column flex={"1"} style={{ minWidth: 0 }}>
            <Row>
              <Text>Company</Text>
            </Row>
            <Row style={{ width: '100%', minWidth: 0 }}>
              <Text
                ref={companyRef}
                $ellipsis="1"
                $faded
                showTooltip={isTextTruncated(companyRef)}
              >
                {getConnectionProfileField(connection.connection, "company")}
              </Text>
            </Row>
          </Column>
          <Column flex={"1"} style={{ minWidth: 0 }}>
            <Row>
              <Text>Location</Text>
            </Row>
            <Row style={{ width: '100%', minWidth: 0 }}>
              <Text
                ref={locationRef}
                $ellipsis="1"
                $faded
                showTooltip={isTextTruncated(locationRef)}
              >
                {getConnectionProfileField(connection.connection, "location")}
              </Text>
            </Row>
          </Column>
        </Row>
      </InfoBoxColumn>
    </CardWrapper>
  );
};

const WrappingRow = styled(Row)`
  display: flex;
  min-height: 24px;
  width: 100%;
  & svg {
    cursor: pointer;
  }
`;

const TopRightActionsRow = styled(Row)`
  align-items: center;
  margin-left: auto;
`;

const CardIconsGroup = styled(Row)<{ $visible: boolean }>`
  margin-right: 8px;
  align-items: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 0.2s ease;
`;

const CardActionIconButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    cursor: default;
  }

  .path {
    fill: ${styles.colors.black300};
  }

  .svg {
    transform: scale(1.2);
    transform-origin: center;
  }

  &:disabled .path {
    fill: ${styles.colors.black200};
  }

  &:hover {
    .path {
      fill: ${styles.colors.primary400};
    }
  }

  &:disabled:hover .path {
    fill: ${styles.colors.black200};
  }
`;

const CardWrapper = styled.div<{ isDragging: boolean }>`
  background: white;
  color: white;
  padding: 12px;
  height: wrap-content;
  min-height: 150px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  cursor: grab;
  position: relative;
  border: 1px solid ${styles.colors.black200};
  ${({ isDragging }) =>
    isDragging && `opacity: 0.3; border: 1px solid ${styles.colors.primary600}`}

  &:hover {
    border: 1px solid ${styles.colors.primary600};

    & .kanban-connection-card-name-field {
      color: ${styles.colors.primary400};
    }
  }

  .kanban-connection-card-name-field {
    cursor: pointer;
  }
`;

const InfoBoxColumn = styled(Column)`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  gap: 8px;
`;
const ProfilePhotoColumn = styled(Column)`
  max-height: 32px;
`;

export default ConnectionCard;
